import { css } from '@emotion/react';
import { graphql } from 'gatsby';
import { FixedObject } from 'gatsby-image';
import React from 'react';
import { Helmet } from 'react-helmet';

import { Footer } from '../components/Footer';
import Pagination from '../components/Pagination';
import { PostCard } from '../components/PostCard';
import { Wrapper } from '../components/Wrapper';
import SiteNav from '../components/header/SiteNav';
import MicrodataHelmet from '../components/seo/MicrodataHelmet';
import Microdata from '../configs/microdata';
import Config from '../configs/websiteConfig';
import { PreviewBlogPost } from '../graphql/blogPostQuery';
import IndexLayout from '../layouts';
import {
  inner,
  outer,
  PostFeed,
  Posts,
  SiteDescription,
  SiteHeaderContent,
  SiteMain,
  SiteTitle,
  SiteHeaderStyles,
} from '../styles/shared';

export interface IndexProps {
  pageContext: {
    currentPage: number;
    numPages: number;
  };
  data: {
    logo: {
      childImageSharp: {
        fixed: FixedObject;
      };
    };
    header: {
      childImageSharp: {
        fixed: FixedObject;
      };
    };
    allMarkdownRemark: {
      edges: Array<{
        node: PreviewBlogPost;
      }>;
    };
  };
}

const IndexPage: React.FC<IndexProps> = props => {
  const { width, height } = props.data.header.childImageSharp.fixed;
  return (
    <IndexLayout css={HomePosts}>
      <Helmet>
        <html lang={Config.lang} />
        <title>{Config.title}</title>
        <meta name="description" content={Config.description} />
        <meta property="og:site_name" content={Config.title} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={Config.title} />
        <meta property="og:description" content={Config.description} />
        <meta property="og:url" content={Config.siteUrl} />
        <meta
          property="og:image"
          content={`${Config.siteUrl}${props.data.header.childImageSharp.fixed.src}`}
        />
        {Config.externalUrls.facebook && (
          <meta property="article:publisher" content={Config.externalUrls.facebook} />
        )}
        {Config.googleSiteVerification && (
          <meta name="google-site-verification" content={Config.googleSiteVerification} />
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={Config.title} />
        <meta name="twitter:description" content={Config.description} />
        <meta name="twitter:url" content={Config.siteUrl} />
        <meta
          name="twitter:image"
          content={`${Config.siteUrl}${props.data.header.childImageSharp.fixed.src}`}
        />
        {Config.externalUrls.twitter && (
          <meta
            name="twitter:site"
            content={`@${Config.externalUrls.twitter.split('https://twitter.com/')[1]}`}
          />
        )}
        <meta property="og:image:width" content={width.toString()} />
        <meta property="og:image:height" content={height.toString()} />
      </Helmet>
      <MicrodataHelmet microdata={Microdata.generateWebsiteMicrodata({})} />
      <Wrapper>
        <div
          css={[outer, SiteHeaderStyles]}
          className="site-header-background"
          style={{
            backgroundImage: `url('${props.data.header.childImageSharp.fixed.src}')`,
          }}
        >
          <div css={inner}>
            <SiteNav isHome />
            <SiteHeaderContent className="site-header-content">
              <SiteTitle className="site-title">
                {props.data.logo && props.data.logo.childImageSharp ? (
                  <img
                    style={{ maxHeight: '55px' }}
                    src={props.data.logo.childImageSharp.fixed.src}
                    alt={Config.title}
                  />
                ) : (
                  Config.title
                )}
              </SiteTitle>
              <SiteDescription>{Config.description}</SiteDescription>
            </SiteHeaderContent>
          </div>
        </div>
        <main id="site-main" css={[SiteMain, outer]}>
          <div css={[inner, Posts]}>
            <div css={[PostFeed]}>
              {props.data.allMarkdownRemark.edges.map((post, index) => {
                // filter out drafts in production
                return (
                  (post.node.frontmatter.draft !== true ||
                    process.env.NODE_ENV !== 'production') && (
                    <PostCard key={post.node.fields.slug} post={post.node} large={index === 0} />
                  )
                );
              })}
            </div>
          </div>
        </main>
        {props.children}
        {props.pageContext.numPages > 1 && (
          <Pagination
            currentPage={props.pageContext.currentPage}
            numPages={props.pageContext.numPages}
          />
        )}
        <Footer />
      </Wrapper>
    </IndexLayout>
  );
};

export const pageQuery = graphql`
  query blogPageQuery($skip: Int!, $limit: Int!) {
    logo: file(absolutePath: { regex: "/theme/logo.png/" }) {
      childImageSharp {
        fixed {
          ...GatsbyImageSharpFixed
        }
      }
    }
    header: file(absolutePath: { regex: "/theme/cover.png/" }) {
      childImageSharp {
        fixed(width: 2000, quality: 100) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    allMarkdownRemark(
      sort: { fields: [frontmatter___date], order: DESC }
      filter: { frontmatter: { draft: { ne: true } }, fileAbsolutePath: { regex: "/(blog)/" } }
      limit: $limit
      skip: $skip
    ) {
      edges {
        node {
          ...PreviewBlogPost
        }
      }
    }
  }
`;

const HomePosts = css`
  @media (min-width: 795px) {
    .post-card-large {
      flex: 1 1 100%;
      flex-direction: row;
      padding-bottom: 40px;
      min-height: 280px;
      border-top: 0;
    }

    .post-card-large .post-card-title {
      margin-top: 0;
      font-size: 3.2rem;
    }

    .post-card-large:not(.no-image) .post-card-header {
      margin-top: 0;
    }

    .post-card-large .post-card-image-link {
      position: relative;
      flex: 1 1 auto;
      margin-bottom: 0;
      min-height: 380px;
    }

    .post-card-large .post-card-image {
      position: absolute;
      width: 100%;
      height: 100%;
    }

    .post-card-large .post-card-content {
      flex: 0 1 361px;
      justify-content: center;
    }

    .post-card-large .post-card-title {
      margin-top: 0;
      font-size: 3.2rem;
    }

    .post-card-large .post-card-content-link {
      padding: 0 0 0 40px;
    }

    .post-card-large .post-card-meta {
      padding: 0 0 0 40px;
    }

    .post-card-large .post-card-excerpt p {
      margin-bottom: 1.5em;
      font-size: 1.8rem;
      line-height: 1.5em;
    }
  }
`;

export default IndexPage;
